import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { clearKnockTarget } from "../lib/draft";
import { meQuery } from "../lib/queries";
import { PreAuthKnock } from "../components/knock/pre-auth";
import { Page } from "../components/ui/page";

/**
 * Somebody else's door (M8, PRD §5.3). `nob.me/@handle` is how a door is
 * handed out, so it is the app's one address a stranger arrives at cold — and
 * arriving signed out is the ordinary case, not the exception.
 *
 * ON ONE ORIGIN THE COLD ARRIVAL IS THE API'S (ruling R-0926-1 (1)). A hard load
 * of `/@handle` is the server-rendered door page, and this screen is reached
 * from it: its "sign in" goes to `/?knock=<handle>`, and a visitor with a
 * session is sent there directly (with `?poster=1` when they asked for it),
 * which the index route turns into this address client-side.
 *
 * WHICH IS NOW THE ONLY CASE THIS SCREEN ANSWERS (owner, 2026-08-26). The poster
 * is an ACQUISITION surface: it exists to explain a door to somebody who has
 * never met one, and every step of it — the plate, the composer that holds your
 * words, the sign-in that sending costs — is a step towards an account. Somebody
 * who already HAS an account needs none of it, so a signed-in arrival goes
 * straight to the console and opens a conversation at this door instead
 * (`shell/compose-pane.tsx`). The handle is already in the knock target, written
 * by the route's `beforeLoad`, and `/` reads it there.
 *
 * WITH ONE EXCEPTION, AND IT IS THE OWNER'S — ONLY THE OWNER'S (item 14,
 * 2026-08-26; scoped by ruling R-0914-2 (2)). `?poster=1` asks for the poster
 * ITSELF, and it is what "See it as strangers do" on the door's settings page
 * opens: before it existed the one person who could not look at a door was the
 * person who hung it, because every route to the plate bounced an authed
 * browser back to the console.
 *
 * A CONSOLE AFFORDANCE IS NOT A PUBLIC URL. The flag is honoured only for the
 * door's own owner; for anybody else — a signed-in stranger, a signed-out
 * visitor — it is simply IGNORED, and the address answers with the ordinary
 * door page for their situation. Not an error and not a refusal: there is
 * nothing to refuse, because there was never anything here that was theirs.
 * Scoped HERE, in the SPA route, rather than on the server-rendered plate,
 * because this is the only layer that reads the flag at all — the api's
 * `pages/door.ts` has never looked at the query string, and a second copy of
 * the rule where the first one does not exist is a rule that goes stale.
 *
 * It is a SIMULATION AND NOT A SANDBOX. The screen is the real one, reading the
 * real public card, so a paused door answers with the closed poster and a
 * suppressed one with its line — that is item 29, and it costs no second code
 * path. What it does not do is fake being signed out: "Knock with X" still does
 * what it does, because a button rewired only for the preview would be the one
 * thing on screen that is not what a stranger gets.
 *
 * What a signed-out arrival meets is the COMPOSER, not a sign-in screen: they
 * came to say something, and asking them to prove who they are before they have
 * written a word gets the order backwards. Signing in is what SENDING costs
 * (`pre-auth.tsx`), the words are held across the round trip, and the stored
 * target catches the return — which comes back to a bare `/`, and now stays
 * there, because by then they are signed in.
 *
 * There is no second way in from here. X is the only identity lane (ruling
 * R-0826-1), so the composer's own button is the whole of it — the quiet link to
 * a sign-in screen with an email form on it went when the email form did.
 *
 * The money step is no longer read here either. It was, so that a signed-in
 * visitor could not reach the intake before answering it; there is no intake at
 * this address any more, and `/` orders its own gates — the run before the
 * knock target — which is where that order belonged all along.
 */
export function KnockRoute() {
  const { handle } = useParams({ from: "/@{$handle}" });
  const { poster } = useSearch({ from: "/@{$handle}" });
  const navigate = useNavigate();
  const { data: me, isPending } = useQuery(meQuery());
  // The flag is the owner's or it is nothing. A session that is not this door's
  // is treated exactly as though the parameter had not been typed.
  const previewing = poster === 1 && me?.xHandle?.toLowerCase() === handle.toLowerCase();

  useEffect(() => {
    if (me && !previewing) void navigate({ to: "/", search: {}, replace: true });
  }, [me, previewing, navigate]);

  // Nothing is said about the door until it is known who is asking: a signed-in
  // arrival is leaving this address and must not be shown a poster on the way —
  // unless it is the poster they came for.
  if (isPending || (me && !previewing)) return <div className="min-h-[40vh]" aria-hidden />;

  return (
    <Page>
      <div className="py-8 sm:py-10">
        <PreAuthKnock
          key={handle}
          handle={handle}
          onLeave={() => {
            // The target must go before the address does: `/@handle` at boot
            // re-aims the visit, and leaving means leaving.
            clearKnockTarget();
            void navigate({ to: "/" });
          }}
        />
      </div>
    </Page>
  );
}
